import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import MatrixDisplay from '../components/MatrixDisplay'
import DashboardDisplay from '../components/DashboardDisplay'
import FIDSDisplay from '../components/FIDSDisplay'
import MapDisplay from '../components/MapDisplay'
import MinimalDisplay from '../components/MinimalDisplay'
import { useGeolocation } from '../hooks/useGeolocation'
import { useFlightData } from '../hooks/useFlightData'

const MODES = {
  matrix: MatrixDisplay,
  dashboard: DashboardDisplay,
  fids: FIDSDisplay,
  map: MapDisplay,
  minimal: MinimalDisplay,
}

export default function Display() {
  const { displayId } = useParams()
  const [searchParams] = useSearchParams()
  const mode = searchParams.get('mode') || 'dashboard'
  const radius = parseInt(searchParams.get('radius') || '20')
  const airport = searchParams.get('airport') || 'LHR'
  const updateInterval = parseInt(searchParams.get('interval') || '10000')

  const urlLat = searchParams.get('lat')
  const urlLon = searchParams.get('lon')
  const hasExplicitLocation = urlLat !== null && urlLon !== null

  // A display created with a configured location (the normal path) should never
  // prompt for browser geolocation - that only makes sense as a fallback for old
  // links created before locations were configurable, or if none was set.
  const browserLocation = useGeolocation(!hasExplicitLocation)
  const location = hasExplicitLocation
    ? { lat: parseFloat(urlLat), lon: parseFloat(urlLon), loading: false, error: null }
    : browserLocation

  const { flights, stats, loading, error } = useFlightData(location, radius, updateInterval)
  const [weather, setWeather] = useState(null)
  
  useEffect(() => {
    const el = document.documentElement
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {})
    
    let timeout
    const hideCursor = () => {
      document.body.style.cursor = 'default'
      clearTimeout(timeout)
      timeout = setTimeout(() => { document.body.style.cursor = 'none' }, 3000)
    }
    document.addEventListener('mousemove', hideCursor)
    document.addEventListener('click', hideCursor)
    return () => {
      document.removeEventListener('mousemove', hideCursor)
      document.removeEventListener('click', hideCursor)
      clearTimeout(timeout)
    }
  }, [])
  
  const DisplayComponent = MODES[mode] || DashboardDisplay
  
  if (mode === 'fids') return <FIDSDisplay airport={airport} />
  
  return (
    <div className="h-screen w-screen overflow-hidden bg-slate-950">
      {loading && flights.length === 0 ? (
        <div className="h-full w-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-flight-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <div className="text-slate-400 text-sm">Acquiring signal...</div>
          </div>
        </div>
      ) : (
        <DisplayComponent flights={flights} stats={stats} weather={weather} location={location} radius={radius} />
      )}
      {error && <div className="absolute top-4 right-4 glass-panel px-4 py-2 text-amber-400 text-xs">{error}</div>}
      <div className="absolute bottom-2 right-2 text-slate-700/30 text-[10px] font-mono">FlightWall // {displayId}</div>
    </div>
  )
}
